import {
  API_ROUTES,
  connectorCatalogSchema,
  connectorConnectionSchema,
  connectorSyncSummarySchema,
  importHistorySchema,
  importPreviewSchema,
  importRunResultSchema,
  importSuggestionSchema,
  oauthStartSchema,
  type ConnectorProvider,
  type ImportColumnMap,
  type ImportOptions,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** Connections to other systems, and spreadsheet imports. */

export const connectionKeys = {
  all: (organizationId: string) => ['connections', organizationId] as const,
  catalog: (organizationId: string) => ['connections', organizationId, 'catalog'] as const,
  imports: (organizationId: string) => ['connections', organizationId, 'imports'] as const,
};

export const connectorCatalogQuery = (organizationId: string) =>
  queryOptions({
    queryKey: connectionKeys.catalog(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.integrations.catalog(organizationId),
        schema: connectorCatalogSchema,
        signal,
      }),
    enabled: organizationId !== '',
    staleTime: 30_000,
  });

export const importHistoryQuery = (organizationId: string) =>
  queryOptions({
    queryKey: connectionKeys.imports(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.imports.history(organizationId),
        schema: importHistorySchema,
        signal,
      }),
    enabled: organizationId !== '',
  });

export const connectApiKey = (
  organizationId: string,
  provider: ConnectorProvider,
  apiKey: string,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.integrations.connection(organizationId, provider),
    body: { apiKey },
    schema: connectorConnectionSchema,
  });

export const disconnectConnector = (organizationId: string, provider: ConnectorProvider) =>
  apiClient.request({
    method: 'DELETE',
    path: API_ROUTES.integrations.connection(organizationId, provider),
    schema: z.object({ disconnected: z.boolean() }),
  });

export const syncConnector = (organizationId: string, provider: ConnectorProvider) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.integrations.sync(organizationId, provider),
    body: {},
    schema: connectorSyncSummarySchema,
  });

export const startOAuth = (organizationId: string, provider: ConnectorProvider) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.integrations.oauthStart(organizationId, provider),
    body: {},
    schema: oauthStartSchema,
  });

/** A file as base64, for the two import routes that read it on the server. */
export async function fileToBase64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < buffer.length; index += chunk) {
    binary += String.fromCharCode(...buffer.subarray(index, index + chunk));
  }
  return btoa(binary);
}

export const previewImport = (organizationId: string, fileName: string, contentBase64: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.imports.preview(organizationId),
    body: { fileName, contentBase64 },
    schema: importPreviewSchema,
  });

export const suggestImport = (organizationId: string, headers: string[], sample: string[][]) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.imports.suggest(organizationId),
    body: { headers, sample },
    schema: importSuggestionSchema,
  });

export const runImport = (
  organizationId: string,
  input: {
    fileName: string;
    contentBase64: string;
    name: string;
    columnMap: ImportColumnMap;
    options: ImportOptions;
    dryRun: boolean;
  },
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.imports.run(organizationId),
    body: input,
    schema: importRunResultSchema,
  });
